import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import TitleSubtitle from "../../../components/common/TitleSubtitle";
import { useNotification } from "../../../context/NotificationContext";
import adminSurface from "../../../components/layouts/AdminSurface.module.css";
import studio from "../../../styles/studioPage.module.css";
import {
    activateAudioVersion,
    analyzeAudioLufs,
    AudioVersion,
    getTrackAudios,
    listAudioVersions,
    NormalizationAudioItem,
    normalizeAudio,
} from "../../../api/audioNormalization";

const DEFAULT_TARGET_LUFS = -16;
const PROBLEMATIC_THRESHOLD = -20;
const TARGET_PRESETS: Array<{ value: number; labelKey?: string }> = Array.from({ length: 9 }, (_, i) => ({
    value: -20 + i,
}));

type RowState = NormalizationAudioItem & {
    busy?: "analyzing" | "normalizing" | "activating" | null;
    error?: string | null;
    normalizedUrl?: string | null;
    normalizedLufs?: number | null;
    normalizedTarget?: number | null;
};

const lufsColor = (lufs: number | null) => {
    if (lufs === null) return "#999";
    if (lufs > -18) return "#1a9d3a";
    if (lufs > -22) return "#d68910";
    return "#c0392b";
};

const formatLufs = (v: number | null | undefined) => (v == null ? "—" : `${v.toFixed(1)} LUFS`);

const boostLabelFor = (targetLufs: number | null, t: any): string => {
    if (targetLufs == null) return "—";
    const preset = TARGET_PRESETS.find((p) => p.value === targetLufs);
    if (preset?.labelKey) return `${t(preset.labelKey)} (~${preset.value} LUFS)`;
    return `${targetLufs} LUFS`;
};

const AudioNormalization: React.FC = () => {
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const { showNotification } = useNotification();

    const [trackIdInput, setTrackIdInput] = useState("");
    const [loading, setLoading] = useState(false);
    const [trackName, setTrackName] = useState<string | null>(null);
    const [rows, setRows] = useState<RowState[]>([]);
    const [targetLufs, setTargetLufs] = useState<number>(DEFAULT_TARGET_LUFS);
    const [versionsModal, setVersionsModal] = useState<{
        audiotrackId: string;
        versions: AudioVersion[];
        activating: boolean;
    } | null>(null);

    const token = localStorage.getItem("token") || "";

    const loadTrack = useCallback(async () => {
        if (!trackIdInput.trim()) return;
        setLoading(true);
        try {
            const res = await getTrackAudios(token, trackIdInput.trim());
            setTrackName(res.track.internalName || res.track.id);
            setRows(
                res.items.map((it) => ({
                    ...it,
                    busy: null,
                    error: null,
                    normalizedUrl: null,
                    normalizedLufs: null,
                })),
            );
        } catch (e: any) {
            showNotification({ message: e?.message || t("audioNormLoadTrackFailed"), type: "error" });
        } finally {
            setLoading(false);
        }
    }, [trackIdInput, token, showNotification, t]);

    const updateRow = useCallback((audiotrackId: string, patch: Partial<RowState>) => {
        setRows((prev) => prev.map((r) => (r.audiotrackId === audiotrackId ? { ...r, ...patch } : r)));
    }, []);

    const onAnalyze = useCallback(
        async (row: RowState) => {
            updateRow(row.audiotrackId, { busy: "analyzing", error: null });
            try {
                const res = await analyzeAudioLufs(token, row.audiotrackId);
                updateRow(row.audiotrackId, {
                    busy: null,
                    lufs: res.lufs,
                    truePeak: res.truePeak,
                    measuredAt: new Date().toISOString(),
                });
            } catch (e: any) {
                updateRow(row.audiotrackId, { busy: null, error: e?.message || t("audioNormAnalyzeFailed") });
            }
        },
        [token, updateRow, t],
    );

    const onNormalize = useCallback(
        async (row: RowState, force = false) => {
            updateRow(row.audiotrackId, { busy: "normalizing", error: null });
            try {
                const res = await normalizeAudio(token, row.audiotrackId, {
                    force,
                    targetLufs,
                });
                if (res.skipped) {
                    showNotification({
                        message: res.reason || t("audioNormSkipped"),
                        type: "info",
                    });
                    updateRow(row.audiotrackId, {
                        busy: null,
                        lufs: res.originalLufs,
                        truePeak: res.originalTruePeak,
                    });
                    return;
                }
                // Do NOT overwrite the row's current-version LUFS — the active audio hasn't changed.
                // Only attach the preview so the admin can compare via Versions / Revert.
                updateRow(row.audiotrackId, {
                    busy: null,
                    normalizedUrl: res.newUrl,
                    normalizedLufs: res.normalizedLufs,
                    normalizedTarget: res.targetLufs,
                });
            } catch (e: any) {
                updateRow(row.audiotrackId, { busy: null, error: e?.message || t("audioNormNormalizeFailed") });
            }
        },
        [token, updateRow, showNotification, t, targetLufs],
    );

    const onOpenVersions = useCallback(
        async (row: RowState) => {
            try {
                const res = await listAudioVersions(token, row.audiotrackId);
                setVersionsModal({
                    audiotrackId: row.audiotrackId,
                    versions: res.versions,
                    activating: false,
                });
            } catch (e: any) {
                showNotification({ message: e?.message || t("audioNormLoadVersionsFailed"), type: "error" });
            }
        },
        [token, showNotification, t],
    );

    const refreshRows = useCallback(
        async (audiotrackIds: string[]) => {
            if (!trackIdInput.trim() || audiotrackIds.length === 0) return;
            try {
                const res = await getTrackAudios(token, trackIdInput.trim());
                setRows((prev) =>
                    prev.map((r) => {
                        if (!audiotrackIds.includes(r.audiotrackId)) return r;
                        const fresh = res.items.find((it) => it.audiotrackId === r.audiotrackId);
                        if (!fresh) return r;
                        // Reset the in-flight preview — activation has consumed it.
                        return {
                            ...r,
                            ...fresh,
                            normalizedUrl: null,
                            normalizedLufs: null,
                            normalizedTarget: null,
                            busy: null,
                            error: null,
                        };
                    }),
                );
            } catch {
                // best-effort refresh — surface nothing if it fails
            }
        },
        [token, trackIdInput],
    );

    const onActivateVersion = useCallback(
        async (audiotrackId: string, target: AudioVersion) => {
            if (!versionsModal) return;
            setVersionsModal({ ...versionsModal, activating: true });
            try {
                await activateAudioVersion(token, audiotrackId, target.url);
                showNotification({ message: t("audioNormVersionActivated"), type: "success" });
                setVersionsModal(null);
                // Refetch authoritative state from backend (URL, isNormalized, version, cached LUFS).
                await refreshRows([audiotrackId]);
            } catch (e: any) {
                showNotification({ message: e?.message || t("audioNormActivateFailed"), type: "error" });
                setVersionsModal((prev) => (prev ? { ...prev, activating: false } : prev));
            }
        },
        [token, showNotification, t, versionsModal, refreshRows],
    );

    const bulkAnalyze = useCallback(async () => {
        for (const r of rows) {
            if (r.lufs != null) continue;
            await onAnalyze(r);
        }
    }, [rows, onAnalyze]);

    const bulkNormalizeProblematic = useCallback(async () => {
        let processed = 0;
        let skipped = 0;
        for (const r of rows) {
            if (r.lufs == null || r.lufs > PROBLEMATIC_THRESHOLD) continue;
            if (r.normalizedUrl && r.normalizedTarget === targetLufs) {
                skipped += 1;
                continue;
            }
            await onNormalize(r, true);
            processed += 1;
        }
        showNotification({
            message: t("audioNormBulkDone", { processed, skipped }),
            type: "info",
        });
    }, [rows, onNormalize, targetLufs, showNotification, t]);

    const bulkActivateNormalized = useCallback(async () => {
        const pending = rows.filter((r) => r.normalizedUrl);
        if (pending.length === 0) return;
        if (!window.confirm(t("audioNormActivateAllConfirm", { count: pending.length }))) return;
        const activatedIds: string[] = [];
        let failed = 0;
        for (const r of pending) {
            updateRow(r.audiotrackId, { busy: "activating", error: null });
            try {
                await activateAudioVersion(token, r.audiotrackId, r.normalizedUrl as string);
                activatedIds.push(r.audiotrackId);
            } catch (e: any) {
                failed += 1;
                updateRow(r.audiotrackId, { busy: null, error: e?.message || t("audioNormActivateFailed") });
            }
        }
        // Single refetch for all activated rows (URL, isNormalized, version, cached LUFS).
        await refreshRows(activatedIds);
        // refreshRows is best-effort — never leave a row stuck in "activating" if it failed.
        setRows((prev) => prev.map((r) => (r.busy === "activating" ? { ...r, busy: null } : r)));
        showNotification({
            message: t("audioNormActivateAllDone", { activated: activatedIds.length, failed }),
            type: failed > 0 ? "info" : "success",
        });
    }, [rows, token, updateRow, refreshRows, showNotification, t]);

    const anyBusy = useMemo(() => rows.some((r) => r.busy), [rows]);
    const pendingCount = useMemo(() => rows.filter((r) => r.normalizedUrl).length, [rows]);
    const measuredCount = useMemo(() => rows.filter((r) => r.lufs != null).length, [rows]);
    const problematicCount = useMemo(
        () => rows.filter((r) => r.lufs != null && r.lufs <= PROBLEMATIC_THRESHOLD).length,
        [rows],
    );

    return (
        <div className={`${adminSurface.audioNormPage} ${adminSurface.pageContent}`}>
            <div className={`${studio.pageTitle} ${adminSurface.pageTitle}`}>
                <TitleSubtitle title={t("audioNorm")} subtitle={t("audioNormSubtitle")} />
            </div>

            <div className={adminSurface.supportLoadBar}>
                <input
                    type="text"
                    placeholder={t("audioNormTrackIdPlaceholder")}
                    value={trackIdInput}
                    onChange={(e) => setTrackIdInput(e.target.value)}
                />
                <button
                    className={adminSurface.supportBtnCoral}
                    onClick={loadTrack}
                    disabled={loading || !trackIdInput.trim()}
                >
                    {loading ? t("audioNormLoading") : t("audioNormLoadTrack")}
                </button>
                {trackName && (
                    <span className={adminSurface.pname}>
                        {t("audioNormTrackLabel")}: <b>{trackName}</b>
                    </span>
                )}
            </div>

            {rows.length > 0 && (
                <div className={adminSurface.supportStatBar}>
                    <span className={adminSurface.stats}>
                        {t("audioNormSummary", {
                            count: rows.length,
                            measured: measuredCount,
                            problematic: problematicCount,
                        })}
                    </span>
                    <button className={adminSurface.supportBtnCoral} onClick={bulkAnalyze} disabled={anyBusy}>
                        {t("audioNormAnalyzeAll")}
                    </button>
                    <button
                        className={adminSurface.supportBtnCoral}
                        onClick={bulkNormalizeProblematic}
                        disabled={problematicCount === 0 || anyBusy}
                    >
                        {t("audioNormNormalizeProblematic", { count: problematicCount })}
                    </button>
                    <button
                        className={adminSurface.supportBtnCoral}
                        onClick={bulkActivateNormalized}
                        disabled={pendingCount === 0 || anyBusy}
                    >
                        {t("audioNormActivateAll", { count: pendingCount })}
                    </button>
                    <label
                        style={{ color: "var(--ink-3)", fontSize: 13, display: "flex", alignItems: "center", gap: 6 }}
                    >
                        {t("audioNormBoostLabel")}:
                        <select value={targetLufs} onChange={(e) => setTargetLufs(parseFloat(e.target.value))}>
                            {TARGET_PRESETS.map((p) => (
                                <option key={p.value} value={p.value}>
                                    {p.labelKey ? `${t(p.labelKey)} (~${p.value} LUFS)` : `${p.value} LUFS`}
                                </option>
                            ))}
                        </select>
                    </label>
                </div>
            )}

            <div
                className={`${adminSurface.tableContainer} ${adminSurface.tblScrollSupport} ${adminSurface.supportScrollX}`}
            >
                <table className={`${adminSurface.table} ${adminSurface.supportNzTable}`}>
                    <thead>
                        <tr>
                            <th>{t("audioNormColPosition")}</th>
                            <th>{t("audioNormColWaypoint")}</th>
                            <th>{t("audioNormColLanguage")}</th>
                            <th>{t("audioNormColStatus")}</th>
                            <th>{t("audioNormColLufs")}</th>
                            <th>{t("audioNormColCurrent")}</th>
                            <th>{t("audioNormColPreview")}</th>
                            <th>{t("audioNormColActions")}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.length === 0 ? (
                            <tr>
                                <td colSpan={8} className={adminSurface.supportEmpty}>
                                    —
                                </td>
                            </tr>
                        ) : (
                            rows.map((r) => (
                                <tr key={r.audiotrackId}>
                                    <td>{r.waypointPosition}</td>
                                    <td>{r.waypointName}</td>
                                    <td>{r.language}</td>
                                    <td>
                                        {r.isNormalized ? (
                                            <span style={{ color: "var(--green-700)", fontWeight: 600 }}>
                                                {t("audioNormStatusNormalized", { version: r.version })}
                                            </span>
                                        ) : (
                                            <span style={{ color: "var(--ink-3)" }}>
                                                {t("audioNormStatusOriginal")}
                                            </span>
                                        )}
                                    </td>
                                    <td style={{ color: lufsColor(r.lufs), fontWeight: 600 }}>{formatLufs(r.lufs)}</td>
                                    <td>
                                        <audio controls src={r.currentUrl} style={{ height: 30, maxWidth: 220 }} />
                                    </td>
                                    <td>
                                        {r.normalizedUrl ? (
                                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                                <audio
                                                    controls
                                                    src={r.normalizedUrl}
                                                    style={{ height: 30, maxWidth: 220 }}
                                                />
                                                <span
                                                    style={{
                                                        fontSize: 12,
                                                        color: lufsColor(r.normalizedLufs ?? null),
                                                        fontWeight: 600,
                                                    }}
                                                >
                                                    {formatLufs(r.normalizedLufs)}
                                                </span>
                                            </div>
                                        ) : (
                                            <span style={{ color: "var(--ink-4)" }}>—</span>
                                        )}
                                    </td>
                                    <td>
                                        <div className={adminSurface.supportNzActs}>
                                            <button
                                                className={`${adminSurface.supportBtnCoral} ${adminSurface.supportBtnCoralSm}`}
                                                onClick={() => onAnalyze(r)}
                                                disabled={anyBusy}
                                            >
                                                {r.busy === "analyzing" ? "…" : t("audioNormBtnAnalyze")}
                                            </button>
                                            <button
                                                className={`${adminSurface.supportBtnCoral} ${adminSurface.supportBtnCoralSm}`}
                                                onClick={() => onNormalize(r)}
                                                disabled={anyBusy}
                                            >
                                                {r.busy === "normalizing" ? "…" : t("audioNormBtnNormalize")}
                                            </button>
                                            <button
                                                className={`${adminSurface.supportBtnCoral} ${adminSurface.supportBtnCoralSm}`}
                                                onClick={() => onOpenVersions(r)}
                                                disabled={anyBusy}
                                            >
                                                {t("audioNormBtnVersions")}
                                            </button>
                                        </div>
                                        {r.error && (
                                            <div style={{ color: "var(--danger)", fontSize: 12, marginTop: 6 }}>
                                                {r.error}
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {versionsModal && (
                <div
                    onClick={() => setVersionsModal(null)}
                    style={{
                        position: "fixed",
                        inset: 0,
                        background: "rgba(0,0,0,0.4)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        zIndex: 1000,
                    }}
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            background: "var(--surface)",
                            padding: 24,
                            borderRadius: "var(--radius-lg)",
                            maxWidth: 800,
                            width: "90%",
                            border: "1px solid var(--line)",
                            boxShadow: "var(--shadow-sm)",
                        }}
                    >
                        <h3 style={{ marginTop: 0 }}>{t("audioNormVersionsTitle")}</h3>
                        <table className={adminSurface.table} style={{ width: "100%" }}>
                            <thead>
                                <tr>
                                    <th>{t("audioNormColVersion")}</th>
                                    <th>{t("audioNormColLufs")}</th>
                                    <th>{t("audioNormColTarget")}</th>
                                    <th>{t("audioNormColPreviewShort")}</th>
                                    <th>{t("audioNormColAction")}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {versionsModal.versions.map((v) => (
                                    <tr key={v.key}>
                                        <td>
                                            {v.label} {v.isActive && <strong>{t("audioNormActive")}</strong>}
                                        </td>
                                        <td style={{ color: lufsColor(v.lufs) }}>{formatLufs(v.lufs)}</td>
                                        <td style={{ color: "var(--ink-3)" }}>
                                            {boostLabelFor(v.targetLufs, t)}
                                            {v.mode && v.mode === "linear" && (
                                                <span style={{ marginLeft: 4, fontSize: 11, color: "var(--ink-4)" }}>
                                                    ({t("audioNormModeLinear")})
                                                </span>
                                            )}
                                        </td>
                                        <td>
                                            <audio controls src={v.url} style={{ height: 30 }} />
                                        </td>
                                        <td>
                                            {!v.isActive && (
                                                <button
                                                    className={`${adminSurface.supportBtnCoral} ${adminSurface.supportBtnCoralSm}`}
                                                    onClick={() => onActivateVersion(versionsModal.audiotrackId, v)}
                                                    disabled={versionsModal.activating}
                                                >
                                                    {t("audioNormActivateThis")}
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <div style={{ marginTop: 16, textAlign: "right" }}>
                            <button className={adminSurface.modifyButton} onClick={() => setVersionsModal(null)}>
                                {t("audioNormClose")}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AudioNormalization;
